import { PATH } from '@/constants';
import { useConfirmSyncWindowMutation, useGetOnboardingStepQuery, useUpdateOnboardingStepMutation } from '@/redux/api/api.caller';
import { handleChangeStep, selectedSyncWindowSelector, stepSelector } from '@/redux/features/welcome.slice';
import { BlockStack, Button } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { welcomeConfig } from '../../config';

const SkipButtonWrap = styled.div`
  .Polaris-Button {
    background: #e4e5e7;
    box-shadow: none;
    border: none;

    &:hover {
      background: #d2d5d8;
    }
  }
`;

interface WelcomeFooterProps {
  skipOnly?: boolean;
  onSkip?: () => void;
  isSkipLoading?: boolean;
}

const STEP_SYNC_WINDOW = 2;

const WelcomeFooter = ({ skipOnly = false, onSkip, isSkipLoading }: WelcomeFooterProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentStep = useSelector(stepSelector);
  const selectedSyncWindow = useSelector(selectedSyncWindowSelector);

  const { data, isLoading: isLoadingGet } = useGetOnboardingStepQuery();
  const [updateStep, { isLoading: isLoadingUpdate }] = useUpdateOnboardingStepMutation();
  const [confirmSyncWindow, { isLoading: isLoadingConfirm }] = useConfirmSyncWindowMutation();

  const getNextLabel = () => {
    if (currentStep === 1) return 'Get started';
    return 'Continue';
  };

  const handleNextStep = async () => {
    if (!data?.data) return;
    const { onboardingStep } = data.data;
    const nextStep = currentStep + 1;
    const isLastStep = currentStep === welcomeConfig.length;

    if (isLastStep) {
      await confirmSyncWindow({ rangeDays: selectedSyncWindow }).unwrap();
      updateStep({ onboardingStep: nextStep, onboardingStatus: true }).then(() => {
        navigate(PATH.DASHBOARD);
      });
      return;
    }

    if (nextStep > onboardingStep) {
      updateStep({ onboardingStep: nextStep });
    }
    dispatch(handleChangeStep(nextStep));
  };

  if (skipOnly) {
    return (
      <BlockStack inlineAlign="center">
        <SkipButtonWrap>
          <Button size="slim" disabled={isSkipLoading} onClick={() => onSkip?.()}>
            Skip
          </Button>
        </SkipButtonWrap>
      </BlockStack>
    );
  }

  return (
    <BlockStack inlineAlign="center">
      <Button
        variant="primary"
        size="large"
        disabled={isLoadingGet}
        loading={isLoadingUpdate || isLoadingConfirm}
        onClick={handleNextStep}
      >
        {getNextLabel()}
      </Button>
    </BlockStack>
  );
};

export default WelcomeFooter;
